// Activity events emitted by content/files (plan.md §VI + §XX.3).
//
// Subject convention:
//   - file.* events           → subjectKind = 'file', subjectId = fileId
//   - folder.* events         → subjectKind = 'folder', subjectId = folderId
//   - upload.completed        → also subjectKind = 'file' (the resulting
//                                file is the durable subject; the upload
//                                row is short-lived and not Linkable)
//
// `groupIds` mirrors the resource_groups attachments at emit time so a
// group-scoped feed can pivot on its own column without joining the
// junction table for every read.
import { z } from 'zod';
import { FilePartition } from './folder.js';
export const FILE_ACTIVITY_KINDS = {
    // Folders.
    FolderCreated: 'folder.created',
    FolderRenamed: 'folder.renamed',
    FolderMoved: 'folder.moved',
    FolderDeleted: 'folder.deleted',
    FolderRestored: 'folder.restored',
    FolderPurged: 'folder.purged',
    FolderTagsChanged: 'folder.tags-changed',
    // Files.
    FileUploaded: 'file.uploaded',
    FileRenamed: 'file.renamed',
    FileMoved: 'file.moved',
    FileDeleted: 'file.deleted',
    FileRestored: 'file.restored',
    FilePurged: 'file.purged',
    FileTagsChanged: 'file.tags-changed',
    FileDescriptionChanged: 'file.description-changed',
    FileVersionCreated: 'file.version-created',
    FileVersionRestored: 'file.version-restored',
    FileCopied: 'file.copied',
    FileShareCreated: 'file.share-created',
    FileShareUpdated: 'file.share-updated',
    FileShareRevoked: 'file.share-revoked',
    FileShareAccessed: 'file.share-accessed',
};
/** Glob patterns. Two are needed because file-shaped events use TWO
 *  subject prefixes — `file.*` and `folder.*` — and subscribers may
 *  want either or both. */
export const FILE_FILE_ACTIVITY_KIND_GLOB = 'file.*';
export const FILE_FOLDER_ACTIVITY_KIND_GLOB = 'folder.*';
const Iso = z.string().datetime({ offset: true });
// ---- folder.* ----------------------------------------------------------
export const FolderCreatedEvent = z.object({
    type: z.literal('folder.created'),
    folderId: z.string().uuid(),
    parentId: z.string().uuid().nullable(),
    ownerId: z.string().uuid(),
    partition: FilePartition,
    name: z.string(),
    occurredAt: Iso,
});
export const FolderRenamedEvent = z.object({
    type: z.literal('folder.renamed'),
    folderId: z.string().uuid(),
    ownerId: z.string().uuid(),
    oldName: z.string(),
    newName: z.string(),
    occurredAt: Iso,
});
export const FolderMovedEvent = z.object({
    type: z.literal('folder.moved'),
    folderId: z.string().uuid(),
    ownerId: z.string().uuid(),
    oldParentId: z.string().uuid().nullable(),
    newParentId: z.string().uuid().nullable(),
    /** Number of descendant folders + files re-pathed. */
    descendantCount: z.number().int().nonnegative(),
    occurredAt: Iso,
});
export const FolderDeletedEvent = z.object({
    type: z.literal('folder.deleted'),
    folderId: z.string().uuid(),
    ownerId: z.string().uuid(),
    /** Number of descendant resources also soft-deleted. */
    descendantCount: z.number().int().nonnegative(),
    occurredAt: Iso,
});
export const FolderRestoredEvent = z.object({
    type: z.literal('folder.restored'),
    folderId: z.string().uuid(),
    ownerId: z.string().uuid(),
    descendantCount: z.number().int().nonnegative(),
    occurredAt: Iso,
});
export const FolderPurgedEvent = z.object({
    type: z.literal('folder.purged'),
    folderId: z.string().uuid(),
    ownerId: z.string().uuid(),
    descendantCount: z.number().int().nonnegative(),
    freedBytes: z.number().int().nonnegative(),
    occurredAt: Iso,
});
export const FolderTagsChangedEvent = z.object({
    type: z.literal('folder.tags-changed'),
    folderId: z.string().uuid(),
    ownerId: z.string().uuid(),
    added: z.array(z.string()),
    removed: z.array(z.string()),
    occurredAt: Iso,
});
// ---- file.* ------------------------------------------------------------
export const FileUploadedEvent = z.object({
    type: z.literal('file.uploaded'),
    fileId: z.string().uuid(),
    ownerId: z.string().uuid(),
    folderId: z.string().uuid().nullable(),
    partition: FilePartition,
    name: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    mimeType: z.string(),
    md5: z.string(),
    /** True when the upload was short-circuited via MD5 dedup. */
    deduped: z.boolean(),
    /** True when the upload was the second+ row sharing the storage_key
     *  (cheaper than `deduped` because the dedup MAY have happened
     *  between two siblings of the same logical file). */
    refcountIncremented: z.boolean(),
    occurredAt: Iso,
});
export const FileRenamedEvent = z.object({
    type: z.literal('file.renamed'),
    fileId: z.string().uuid(),
    ownerId: z.string().uuid(),
    oldName: z.string(),
    newName: z.string(),
    occurredAt: Iso,
});
export const FileMovedEvent = z.object({
    type: z.literal('file.moved'),
    fileId: z.string().uuid(),
    ownerId: z.string().uuid(),
    oldFolderId: z.string().uuid().nullable(),
    newFolderId: z.string().uuid().nullable(),
    occurredAt: Iso,
});
export const FileDeletedEvent = z.object({
    type: z.literal('file.deleted'),
    fileId: z.string().uuid(),
    ownerId: z.string().uuid(),
    /** True when the bytes are still in storage (other rows reference). */
    bytesRetained: z.boolean(),
    occurredAt: Iso,
});
export const FileRestoredEvent = z.object({
    type: z.literal('file.restored'),
    fileId: z.string().uuid(),
    ownerId: z.string().uuid(),
    occurredAt: Iso,
});
export const FilePurgedEvent = z.object({
    type: z.literal('file.purged'),
    fileId: z.string().uuid(),
    ownerId: z.string().uuid(),
    freedBytes: z.number().int().nonnegative(),
    /** True when this was the last row referencing the storage_key. */
    storageDeleted: z.boolean(),
    occurredAt: Iso,
});
export const FileTagsChangedEvent = z.object({
    type: z.literal('file.tags-changed'),
    fileId: z.string().uuid(),
    ownerId: z.string().uuid(),
    added: z.array(z.string()),
    removed: z.array(z.string()),
    occurredAt: Iso,
});
export const FileDescriptionChangedEvent = z.object({
    type: z.literal('file.description-changed'),
    fileId: z.string().uuid(),
    ownerId: z.string().uuid(),
    occurredAt: Iso,
});
export const FileVersionCreatedEvent = z.object({
    type: z.literal('file.version-created'),
    fileId: z.string().uuid(),
    ownerId: z.string().uuid(),
    versionNo: z.number().int().min(1),
    sizeBytes: z.number().int().nonnegative(),
    md5: z.string(),
    occurredAt: Iso,
});
export const FileVersionRestoredEvent = z.object({
    type: z.literal('file.version-restored'),
    fileId: z.string().uuid(),
    ownerId: z.string().uuid(),
    /** The version that was restored TO the head of the file. */
    fromVersionNo: z.number().int().min(1),
    /** The new monotonic version number. */
    newVersionNo: z.number().int().min(1),
    occurredAt: Iso,
});
export const FileCopiedEvent = z.object({
    type: z.literal('file.copied'),
    sourceFileId: z.string().uuid(),
    newFileId: z.string().uuid(),
    ownerId: z.string().uuid(),
    occurredAt: Iso,
});
export const FileShareCreatedEvent = z.object({
    type: z.literal('file.share-created'),
    fileId: z.string().uuid(),
    shareId: z.string().uuid(),
    ownerId: z.string().uuid(),
    expiresAt: Iso.nullable(),
    allowDownload: z.boolean(),
    hasPasscode: z.boolean(),
    occurredAt: Iso,
});
export const FileShareUpdatedEvent = z.object({
    type: z.literal('file.share-updated'),
    fileId: z.string().uuid(),
    shareId: z.string().uuid(),
    ownerId: z.string().uuid(),
    changed: z.array(z.string()),
    occurredAt: Iso,
});
export const FileShareRevokedEvent = z.object({
    type: z.literal('file.share-revoked'),
    fileId: z.string().uuid(),
    shareId: z.string().uuid(),
    ownerId: z.string().uuid(),
    occurredAt: Iso,
});
export const FileShareAccessedEvent = z.object({
    type: z.literal('file.share-accessed'),
    fileId: z.string().uuid(),
    shareId: z.string().uuid(),
    ownerId: z.string().uuid(),
    action: z.enum(['view', 'download']),
    success: z.boolean(),
    failureReason: z.string().max(120).nullable(),
    occurredAt: Iso,
});
//# sourceMappingURL=events.js.map